import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const SubscriptionContext = createContext();

export function useSubscription() {
  return useContext(SubscriptionContext);
}

export function SubscriptionProvider({ children }) {
  const { currentUser } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingSubscription, setCheckingSubscription] = useState(false);

  const API_BASE = 'https://klorah-fast-server.onrender.com/api';

  // Check subscription status
  const checkSubscription = async () => {
    if (!currentUser) {
      setSubscription(null);
      setLoading(false);
      return;
    }

    try {
      setCheckingSubscription(true);
      const response = await fetch(`${API_BASE}/sessions/subscription/status`, {
        headers: {
          'instructor-id': currentUser.uid
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSubscription(data.subscription);
      } else {
        setSubscription(null);
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
      setSubscription(null);
    } finally {
      setLoading(false);
      setCheckingSubscription(false);
    }
  };

  // Create subscription
  const createSubscription = async (planType) => {
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    try {
      const response = await fetch(`${API_BASE}/sessions/subscription/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'instructor-id': currentUser.uid
        },
        body: JSON.stringify({
          planType: planType // 'monthly' or 'yearly'
        })
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create subscription');
      }
    } catch (error) {
      console.error('Error creating subscription:', error);
      throw error;
    }
  };

  // Cancel subscription
  const cancelSubscription = async () => {
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    try {
      const response = await fetch(`${API_BASE}/sessions/subscription/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'instructor-id': currentUser.uid
        }
      });

      if (response.ok) {
        await checkSubscription(); // Refresh subscription status
        return true;
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to cancel subscription');
      }
    } catch (error) {
      console.error('Error canceling subscription:', error);
      throw error;
    }
  };

  // Check if user has active subscription
  const hasActiveSubscription = () => {
    return subscription && subscription.status === 'active';
  };

  // Check if subscription is in trial
  const isInTrial = () => {
    return subscription && subscription.status === 'trialing';
  };

  // Get subscription plan details
  const getPlanDetails = () => {
    if (!subscription) return null;
    
    return {
      planType: subscription.planType,
      status: subscription.status,
      currentPeriodEnd: subscription.currentPeriodEnd,
      cancelAtPeriodEnd: subscription.cancelAtPeriodEnd
    };
  };

  useEffect(() => {
    if (currentUser) {
      checkSubscription();
    } else {
      setSubscription(null);
      setLoading(false);
    }
  }, [currentUser]);

  const value = {
    subscription,
    loading,
    checkingSubscription,
    hasActiveSubscription,
    isInTrial,
    getPlanDetails,
    createSubscription,
    cancelSubscription,
    checkSubscription
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
} 